import {
  Card,
  Row,
  Col,
  Switch,
  QRCode,
  Button,
  ConfigProvider,
  Input,
  Select,
  Collapse,
  Modal,
} from "antd";
import Collapsible from "../Collapsible/Collapsible";
import styles from "./Security.module.css";
import { useEffect, useState } from "react";
import { revokeOtherSessions } from "../../../services/security.service";
import { getCookie } from "../../../utils/cookies";
import { showToast } from "../../Toaster/Toaster";
import {
  getAuth,
  multiFactor,
  onAuthStateChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import {
  CountryCode,
  getCountries,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
export const Security = () => {
  const [showTotpEnable, setShowTotpEnable] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showSmsEnable, setShowSmsEnable] = useState(false);
  const [nextStage, setNextStage] = useState(false);
  const [totpUri, setTotpUri] = useState("");
  const [smsLoading, setSmsLoading] = useState(false);
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const auth = getAuth();
  const [totpEnrolled, setTotpEnrolled] = useState(false);
  const [smsEnrolled, setSmsEnrolled] = useState(false);
  const [totpMfaEnrollmentId, setTotpMfaEnrollmentId] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsMfaEnrollmentId, setSmsMfaEnrollmentId] = useState("");
  const [countryCode, setCountryCode] = useState<CountryCode>("GB");
  const mfaEnrolledFactors = JSON.parse(localStorage.getItem("mfa_enrolled_factors") as string);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokedAt, setRevokedAt] = useState<string | null>(null);

  const handleRevokeOtherSessions = () => {
    Modal.confirm({
      title: "Sign out all other devices?",
      content: "This will sign out all other browsers and devices. Your current session will not be affected.",
      okText: "Sign out all others",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        setRevokeLoading(true);
        try {
          const firebaseToken = getCookie("firebase_token") || "";
          const result = await revokeOtherSessions(firebaseToken);
          setRevokedAt(result.revokedAt);
          showToast("All other sessions have been signed out.", { type: "success" });
        } catch {
          showToast("Failed to revoke sessions. Please try again.", { type: "error" });
        } finally {
          setRevokeLoading(false);
        }
      },
    });
  };

  const toggleTotpSwitch = () => {
    if (!showTotpEnable) {
      generateTotp();
    } else {
      setShowTotpEnable(false);
    }
  };

  const toggleSmsSwitch = () => {
    if (!showSmsEnable) {
      setShowSmsEnable(true);
    } else {
      setShowSmsEnable(false);
    }
  };
  const generateTotp = async () => {
    try {
      setTotpLoading(true);
      setCurrentUser(auth.currentUser as any);
      const multiFactorSession = await multiFactor(
        auth.currentUser as any
      ).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(
        multiFactorSession
      );
      setTotpSecret(totpSecret as any);
      const totpUri = totpSecret.generateQrCodeUrl(
        auth?.currentUser?.email as string,
        "Care Hires"
      );
      setTotpUri(totpUri);
      setTotpLoading(false);
      setShowTotpEnable(true);
    } catch (error: any) {
      setTotpLoading(false);
      if (error.code === "auth/requires-recent-login") {
        showToast(
          "Please reauthenticate to perform this action. Please log in again.",
          { type: "error" }
        );
      } else {
        showToast(error.message, { type: "error" });
      }
    }
  };

  const countryOptions = getCountries().map((country) => ({
    value: country as CountryCode,
    label: country,
  }));

  const onCountryChange = (value: CountryCode) => {
    setCountryCode(value);
  };

  const generateSms = async () => {
    try {
      if (phoneNumber) {
        setSmsLoading(true);
        const recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container-id",
          { size: "invisible" }
        );

        let secondFactorPhoneNumber = parsePhoneNumberFromString(
          phoneNumber,
          countryCode
        )?.formatInternational();
        secondFactorPhoneNumber = secondFactorPhoneNumber?.replace(/\s/g, "");
        const multiFactorSession = await multiFactor(
          auth.currentUser as any
        ).getSession();
        const phoneInfoOptions = {
          phoneNumber: secondFactorPhoneNumber,
          session: multiFactorSession,
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier
        );
        setSmsLoading(false);
        showToast("OTP sent successfully", { type: "success" });
        setNextStage(true);
        setVerificationId(verificationId);
      } else {
        showToast("Please enter phone number", { type: "error" });
        return;
      }
    } catch (error: any) {
      setSmsLoading(false);
      if (error.code === "auth/requires-recent-login") {
        showToast(
          "Please reauthenticate to perform this action. Please log in again.",
          { type: "error" }
        );
      } else if (
        error.message === "reCAPTCHA has already been rendered in this element"
      ) {
        showToast(
          "There was an issue with reCAPTCHA verification. Please reload the page and try again.",
          { type: "error" }
        );
      } else if (error.code === "auth/invalid-phone-number") {
        showToast(
          "The phone number you entered is invalid. Please check the format and try again.",
          { type: "error" }
        );
      } else {
        console.log("error", error);
        showToast(error.message, { type: "error" });
      }
    }
  };

  const enableTotp = async () => {
    try {
      setTotpLoading(true);
      const multiFactorAssertion =
        TotpMultiFactorGenerator.assertionForEnrollment(
          totpSecret as any,
          totpCode
        );

      await multiFactor(currentUser as any).enroll(
        multiFactorAssertion,
        "Time-based One-Time Password"
      );
      setTotpLoading(false);

      await auth.currentUser?.reload();

      const updatedMFAInfo = multiFactor(auth.currentUser as any);
      const newEnrollment = updatedMFAInfo.enrolledFactors.find(factor => factor.factorId === "totp");
      const existingFactors = Array.isArray(mfaEnrolledFactors) ? mfaEnrolledFactors : [];
      const updateMFAFactors = [
        ...existingFactors,
        { factorId: "totp", enrollmentId: newEnrollment?.uid },
      ];
      localStorage.setItem(
        "mfa_enrolled_factors",
        JSON.stringify(updateMFAFactors)
      );
      showToast("TOTP MFA Enabled", { type: "success" });
      await checkMFAStatus();
      window.location.reload();
    } catch (error: any) {
      setTotpLoading(false);
      showToast(error.message, { type: "error" });
    }
  };

  const enableSms = async () => {
    try {
      setSmsLoading(true);
      console.log("verificationId", verificationId);
      console.log("smsCode", smsCode);
      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(auth.currentUser as any).enroll(
        multiFactorAssertion,
        "SMS"
      );
      setSmsLoading(false);
      await auth.currentUser?.reload();

      const updatedMFAInfo = multiFactor(auth.currentUser as any);
      const newEnrollment = updatedMFAInfo.enrolledFactors.find(factor => factor.factorId === "phone");
      const existingFactors = Array.isArray(mfaEnrolledFactors) ? mfaEnrolledFactors : [];
      const updateMFAFactors = [
        ...existingFactors,
        { factorId: "phone", enrollmentId: newEnrollment?.uid },
      ];
      localStorage.setItem(
        "mfa_enrolled_factors",
        JSON.stringify(updateMFAFactors)
      );
      showToast("SMS MFA Enabled", { type: "success" });
      await checkMFAStatus();
      window.location.reload();
    } catch (error: any) {
      setSmsLoading(false);
      console.log("error", error);
      showToast(error.message, { type: "error" });
    }
  };
  const disableTotp = async () => {
    try {
      setTotpLoading(true);
      await multiFactor(auth.currentUser as any).unenroll(totpMfaEnrollmentId);
      setTotpLoading(false);
      showToast("TOTP MFA Disabled", { type: "success" });
      const updateMFAFactors = mfaEnrolledFactors?.filter((factor: any) => factor.factorId !== "totp");
      localStorage.setItem(
        "mfa_enrolled_factors",
        JSON.stringify(updateMFAFactors)
      );
      window.location.reload();
    } catch (error: any) {
      setTotpLoading(false);
      if (error.code === "auth/requires-recent-login") {
        showToast(
          "Please reauthenticate to perform this action. Please log in again.",
          { type: "error" }
        );
      } else {
        console.log("error", error);
        showToast(error.message, { type: "error" });
      }
    }
  };

  const disableSms = async () => {
    try {
      setSmsLoading(true);
      await multiFactor(auth.currentUser as any).unenroll(smsMfaEnrollmentId);
      setSmsLoading(false);
      showToast("Sms MFA Disabled", { type: "success" });
      const updateMFAFactors = mfaEnrolledFactors?.filter((factor: any) => factor.factorId !== "phone");
      localStorage.setItem(
        "mfa_enrolled_factors",
        JSON.stringify(updateMFAFactors)
      );
      window.location.reload();
    } catch (error: any) {
      setSmsLoading(false);
      if (error.code === "auth/requires-recent-login") {
        showToast(
          "Please reauthenticate to perform this action. Please log in again.",
          { type: "error" }
        );
      } else {
        showToast(error.message, { type: "error" });
      }
    }
  };

  const checkMFAStatus = async () => {
    const user = auth.currentUser as any;
    if (user) {
      try {
        const totpFactor = user.reloadUserInfo?.mfaInfo?.find(
          (factor: any) => factor.displayName === "Time-based One-Time Password"
        );
        if (totpFactor) {
          setTotpMfaEnrollmentId(totpFactor.mfaEnrollmentId);
          setTotpEnrolled(true);
        } else if (mfaEnrolledFactors?.find((f: any) => f.factorId == "totp")) {
          setTotpEnrolled(true);
          setTotpMfaEnrollmentId(mfaEnrolledFactors?.find((f: any) => f.factorId == "totp")?.enrollmentId);
        } else {
          setTotpEnrolled(false);
        }
        console.log("user.reloadUserInfo", user.reloadUserInfo);
        const smsFactor = user.reloadUserInfo?.mfaInfo?.find(
          (factor: any) =>
            factor.displayName === "SMS" || factor.displayName === ""
        );
        if (smsFactor) {
          setSmsMfaEnrollmentId(smsFactor.mfaEnrollmentId);
          setSmsEnrolled(true);
        } else if (mfaEnrolledFactors?.find((f: any) => f.factorId == "phone")) {
          setSmsEnrolled(true);
          setSmsMfaEnrollmentId(mfaEnrolledFactors?.find((f: any) => f.factorId == "phone")?.enrollmentId);
        } else {
          setSmsEnrolled(false);
        }
      } catch (error) {
        console.error("Error checking MFA status:", error);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      checkMFAStatus();
    });
    return () => unsubscribe();
  }, []);

  return (
    <div
      style={{ backgroundColor: "white" }}
      className={styles["security-container"]}
    >
      <div className={styles["scrollable-container"]}>
        <div style={{ padding: "0 20px" }}>
          <Collapsible
            title="Multi-Factor Authentication (MFA)"
            expanded={true}
            mfaEnabled={totpEnrolled || smsEnrolled}
          >
            <div className="row mt-4">
              <div className="col-md-6">
                <Card>
                  <Row>
                    <Col span={20}>
                      <p className={styles["mfa-type-title"]}>Authenticator App</p>
                      {(!showTotpEnable && !totpEnrolled) && (
                        <>
                          <p className={styles["mfa-type-description"]}>
                            If you don’t already have an authentication app, please
                            install it on your device following the instructions below.
                          </p>
                          <p className={styles["mfa-type-description"]}>
                            Once installed enable and scan the QR Code.
                          </p></>
                      )}
                      {totpEnrolled && (
                        <>
                        <p className={styles["mfa-type-description"]}>
                        MFA enabled via the Authenticator App for your account. 
                          </p>
                          <p className={styles["mfa-type-description"]} style={{fontSize: "12px"}}>
                          You can disable it anytime if you want
                          </p>
                        </>
                      )}
                    </Col>
                    <Col span={4}>
                      {!totpEnrolled ? (
                        <Switch
                          // disabled={showSmsEnable}
                          className={styles["switch"]}
                          onClick={toggleTotpSwitch}
                          loading={totpLoading}
                          checked={showTotpEnable}
                        />
                      ) : (
                        <Button danger onClick={disableTotp} loading={totpLoading}>
                          Disable
                        </Button>
                      )}
                    </Col>
                  </Row>
                  {(!showTotpEnable && !totpEnrolled) && (
                    <div style={{ backgroundColor: "rgb(243 247 251)", borderRadius: "10px", padding: "10px" }}>
                      <p style={{ fontWeight: "600" }}>To Download an Authenticator App</p>
                      <p style={{ marginBottom: "0px" }}>1) Go to Google Play Store (Android) or Apple App Store (iPhone).</p>
                      <p style={{ marginBottom: "0px" }}>2) Search for Google Authenticator, Microsoft Authenticator, or Authy.</p>
                      <p >3) Tap install.</p>
                      <p style={{ whiteSpace: "normal" }}>Once installed Enable the toggle above, then open the app and scan the QR code to proceed to next steps.</p>
                    </div>
                  )}

                  {showTotpEnable && !nextStage && (
                    <div>
                      <p className={styles["totp-text"]}>
                        Scan the following code through the Authenticator App
                      </p>
                      <QRCode value={totpUri} size={200} />
                      <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          ><Button
                            size="large"
                            className={styles["next-btn"]}
                            onClick={toggleTotpSwitch}
                            style={{ marginRight: '10px' }}
                          >
                              Cancel
                            </Button></ConfigProvider>
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          >
                            <Button
                              size="large"
                              className={styles["next-btn"]}
                              onClick={() => setNextStage(true)}
                            >
                              Next
                            </Button>
                          </ConfigProvider>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {showTotpEnable && nextStage && (
                    <div>
                      <p className={styles["totp-text"]}>
                        Enter the 6-digit code from the Authenticator App
                      </p>
                      <Input
                        style={{ width: "50%" }}
                        name="totpCode"
                        placeholder="Enter code"
                        type="number"
                        onChange={(e) => setTotpCode(e.target.value)}
                      />
                      <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          >
                            <Button
                              onClick={enableTotp}
                              size="large"
                              className={styles["next-btn"]}
                              loading={totpLoading}
                            >
                              Done
                            </Button>
                          </ConfigProvider>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Card>
              </div>
              <div className="col-md-6">
                <Card>
                  <Row>
                    <Col span={20}>
                      <p className={styles["mfa-type-title"]}>Text message</p>
                      {(!nextStage && !smsEnrolled) && (
                        <p className={styles["mfa-type-description"]}>
                          Enable and enter the phone number to setup Text Message based authentication
                        </p>
                      )}
                      {smsEnrolled && (
                        <>
                        <p className={styles["mfa-type-description"]}>
                        MFA enabled via Text messages for your account. 
                      </p>
                        <p className={styles["mfa-type-description"]} style={{fontSize: "12px"}}>
                        You can disable it anytime if you want
                        </p></>
                      )}
                    </Col>
                    <Col span={4}>
                      {!smsEnrolled ? (
                        <Switch
                          // disabled={showTotpEnable}
                          className={styles["switch"]}
                          onClick={toggleSmsSwitch}
                          loading={smsLoading}
                          checked={showSmsEnable}
                        />
                      ) : (
                        <Button danger onClick={disableSms} loading={smsLoading}>
                          Disable
                        </Button>
                      )}
                    </Col>
                  </Row>
                  {showSmsEnable && !nextStage && (
                    <div>
                      <p className={styles["totp-text"]}>Phone Number</p>
                      <Select
                        showSearch
                        placeholder="Select country code"
                        optionFilterProp="label"
                        onChange={onCountryChange}
                        options={countryOptions}
                        value={countryCode}
                        style={{ width: 100, marginRight: 10 }}
                      />
                      <Input
                        style={{ width: "50%" }}
                        name="phone"
                        placeholder="Phone Number"
                        onChange={(e) => setPhoneNumber(e.target.value)}
                      />
                      <Row style={{ display: "flex", justifyContent: "flex-end" }} className="mt-3">
                        <Col style={{ display: "flex", alignItems: "center" }}>
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          ><Button
                            // loading={smsLoading}
                            onClick={toggleSmsSwitch}
                            size="large"
                            className={styles["next-btn"]}
                            style={{ marginRight: '10px' }}
                          >
                              Cancel
                            </Button></ConfigProvider>

                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          >
                            <Button
                              loading={smsLoading}
                              onClick={generateSms}
                              size="large"
                              className={styles["next-btn"]}
                            >
                              {" "}
                              Next
                            </Button>
                          </ConfigProvider>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {showSmsEnable && nextStage && (
                    <div>
                      <p className={styles["totp-text"]}>Enter verification code</p>
                      <Input
                        style={{ width: "50%" }}
                        name="totpCode"
                        placeholder="Enter code"
                        type="number"
                        onChange={(e) => setSmsCode(e.target.value)}
                      />
                      <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                          <ConfigProvider
                            theme={{
                              components: {
                                Button: {
                                  paddingInlineLG: 40,
                                  paddingBlockLG: 10,
                                  fontWeight: 600,
                                },
                              },
                              token: {
                                borderRadiusLG: 4,
                              },
                            }}
                          >
                            <Button
                              onClick={enableSms}
                              size="large"
                              className={styles["next-btn"]}
                              loading={smsLoading}
                            >
                              Done
                            </Button>
                          </ConfigProvider>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </Collapsible>
          {/* <Collapsible title="Active Sessions" expanded={true}>
            <div style={{ padding: "16px 0" }}>
              <p style={{ marginBottom: 8 }}>
                Sign out all browsers and devices except this one. Use this if you think your account has been accessed without your permission.
              </p>
              {revokedAt && (
                <p style={{ color: "#52c41a", marginBottom: 12, fontSize: 13 }}>
                  All other sessions were signed out at {new Date(revokedAt).toLocaleString()}.
                </p>
              )}
              <Button
                danger
                loading={revokeLoading}
                onClick={handleRevokeOtherSessions}
              >
                Sign out all other devices
              </Button>
            </div>
          </Collapsible> */}
          <div id="recaptcha-container-id"></div>
        </div>
      </div>
    </div>
  );
};

export default Security;
